import { useMemo } from 'react';
import { Header } from 'semantic-ui-react';
import { ERROR_PROMPTING_IMPACT_FIELD_PROPS } from '../../../../../../constants/generalConstants';
import { ImpactType } from '../../../../../../types';
import { useImportTableStoreDescriptorsMap } from '../../../../context/importTableContext';
import './styles.css';
import { getImpactTableModel, getReportCellClasses, hasErrorPromptingFieldProps } from './utils';
import { useParams } from 'react-router-dom';
import Assert from '../../../../../../../../../utils/asserts';

type ImpactTablePropsType = {
    impact: ImpactType;
};

const ImpactTable = ({ impact }: ImpactTablePropsType) => {
    const params = useParams();
    const importId = Number(params.ID);
    const descriptorsMap = useImportTableStoreDescriptorsMap();
    const tableModel = useMemo(() => {
        return getImpactTableModel(impact, importId);
    }, [impact]);

    const displayWarningMessage = useMemo(() => hasErrorPromptingFieldProps(tableModel), [tableModel]);

    return (
        <>
            {displayWarningMessage && (
                <Header as={'h4'} color="red">
                    Unexpected change in one ore more{' '}
                    {ERROR_PROMPTING_IMPACT_FIELD_PROPS.map((field) => `"${field}"`).join(', ')} properties. Please
                    verify the changes!
                </Header>
            )}
            <table className="extraction-table impact-table">
                <thead>
                    <tr>
                        <th>Fields</th>
                        <th>fields Props</th>
                        {impact.updatedReports.map((r) => (
                            <th key={r.id}>
                                <p>{r.year}</p>
                                <p>{r.period}</p>
                                <p>{r.version}</p>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {tableModel.rows.map(({ descriptorId, props }) => {
                        return props.map(({ propName, reportToPropDiff }, propIndex) => {
                            const targetDescriptor = impact.rowMappings.find((m) => m.descriptorId == descriptorId);
                            let rowLabel;
                            if (targetDescriptor) {
                                rowLabel = descriptorsMap[targetDescriptor.rowId].label;
                            }else {
                                // There is a bug on the server and on the ui where row mappings and descriptors map do not contain 
                                // static fields, descriptors map is made from import table descriptors where there the static fields
                                // are also missing 
                                // This is a temporary solution since we do not expect any diffs in other static fields except src
                                Assert.trueVal(
                                    descriptorId === "src",
                                    `Descriptor with id ${descriptorId} not found in impact row mappings`,
                                );
                                rowLabel = descriptorId;
                            }
                            
                            return (
                                <tr key={`${descriptorId}${propName}`}>
                                    {propIndex === 0 && <td rowSpan={props.length}>{rowLabel}</td>}
                                    <td>{propName}</td>
                                    {impact.updatedReports.map((r) => {
                                        const propDiff = reportToPropDiff[r.id];
                                        return (
                                            <DiffCell
                                                key={r.id}
                                                originalValue={propDiff.originalValue}
                                                modifiedValue={propDiff.modifiedValue}
                                                requireManualCheck={propDiff.requireManualCheck}
                                            />
                                        );
                                    })}
                                </tr>
                            );
                        });
                    })}
                </tbody>
            </table>
        </>
    );
};

type DiffCellPropsType = {
    originalValue: any;
    modifiedValue: any;
    requireManualCheck: boolean;
};

function DiffCell({ originalValue, modifiedValue, requireManualCheck }: DiffCellPropsType) {
    return (
        <td className={getReportCellClasses(requireManualCheck)}>
            <div>
                <span className="field__prop-value field__prop-value--original">
                    {originalValue !== undefined && String(originalValue)}
                </span>
                <span className="field__prop-value field__prop-value--modified">
                    {modifiedValue !== undefined && String(modifiedValue)}
                </span>
            </div>
        </td>
    );
}

export default ImpactTable;
